import { create } from 'zustand';

export interface UploadedFileData {
  id: string;
  name: string;
  rowCount: number;
  columns: string[];
  data: Record<string, unknown>[];
}

interface UploadedFileState {
  /** Per-node files: nodeId → UploadedFileData */
  files: Record<string, UploadedFileData>;
  isUploading: boolean;

  /**
   * Backward-compat derived value.
   * Always equals the first entry in `files`, or null when empty.
   * Legacy consumers (AIDrawer, DataArea) use this to show "the" current file.
   */
  uploadedFile: UploadedFileData | null;

  // Per-node API
  setFileForNode: (nodeId: string, file: UploadedFileData | null) => void;
  clearFileForNode: (nodeId: string) => void;
  clearAllFiles: () => void;
  setIsUploading: (v: boolean) => void;

  // Legacy aliases kept for components that don't know about nodeId
  setUploadedFile: (file: UploadedFileData | null) => void;
  clearFile: () => void;
}

function firstFile(files: Record<string, UploadedFileData>): UploadedFileData | null {
  const values = Object.values(files);
  return values.length > 0 ? values[0] : null;
}

export const useUploadedFileStore = create<UploadedFileState>()((set) => ({
  files: {},
  isUploading: false,
  uploadedFile: null,

  setFileForNode: (nodeId, file) =>
    set((state) => {
      const newFiles =
        file === null
          ? Object.fromEntries(Object.entries(state.files).filter(([k]) => k !== nodeId))
          : { ...state.files, [nodeId]: file };
      return { files: newFiles, uploadedFile: firstFile(newFiles) };
    }),

  clearFileForNode: (nodeId) =>
    set((state) => {
      const newFiles = Object.fromEntries(
        Object.entries(state.files).filter(([k]) => k !== nodeId)
      );
      return { files: newFiles, uploadedFile: firstFile(newFiles) };
    }),

  clearAllFiles: () => set({ files: {}, uploadedFile: null }),

  setIsUploading: (v) => set({ isUploading: v }),

  // Legacy: stores under the special '__global__' key
  setUploadedFile: (file) =>
    set((state) => {
      const newFiles =
        file === null
          ? Object.fromEntries(Object.entries(state.files).filter(([k]) => k !== '__global__'))
          : { ...state.files, __global__: file };
      return { files: newFiles, uploadedFile: firstFile(newFiles) };
    }),

  clearFile: () =>
    set((state) => {
      const newFiles = Object.fromEntries(
        Object.entries(state.files).filter(([k]) => k !== '__global__')
      );
      return { files: newFiles, uploadedFile: firstFile(newFiles) };
    }),
}));
